import { FormatSimple, Tolgee } from '@tolgee/react';

export const SUPPORTED_LANGUAGES = ['fr', 'en'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];
export const DEFAULT_LANGUAGE: SupportedLanguage = 'fr';

const LANGUAGE_STORAGE_KEY = 'tolgee_language';

export function getStoredLanguage(): SupportedLanguage {
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  // Only an explicit choice (LanguageSelector) switches the language. The browser
  // language is deliberately ignored: the app is French by default, and a large part
  // of the UI is not translated yet, so auto-switching to English gave a mixed UI.
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (stored && SUPPORTED_LANGUAGES.includes(stored as SupportedLanguage)) {
    return stored as SupportedLanguage;
  }

  return DEFAULT_LANGUAGE;
}

const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur'] as const;

export function isRtlLanguage(lang: string): boolean {
  return RTL_LANGUAGES.includes(lang as (typeof RTL_LANGUAGES)[number]);
}

export function getLanguageDir(lang: string): 'rtl' | 'ltr' {
  return isRtlLanguage(lang) ? 'rtl' : 'ltr';
}

export function storeLanguage(lang: SupportedLanguage): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = getLanguageDir(lang);
  }
}

export function createTolgeeInstance() {
  return Tolgee()
    .use(FormatSimple())
    .init({
      language: getStoredLanguage(),
      defaultLanguage: DEFAULT_LANGUAGE,
      fallbackLanguage: DEFAULT_LANGUAGE,
      availableLanguages: [...SUPPORTED_LANGUAGES],
      staticData: {
        fr: () => import('../../../../public/i18n/fr.json').then((m) => m.default),
        en: () => import('../../../../public/i18n/en.json').then((m) => m.default),
      },
    });
}
